/**
 * Phase K.3 — StatementPayModal (2026-08-06)
 *
 * Adapted from InvoicePayModal.jsx. Customer-facing Stripe Elements ACH
 * pay flow for a consolidated statement.
 *
 * Contract:
 *   <StatementPayModal
 *     statement={statement}          // customer-shaped statement dict
 *     open={open}
 *     onOpenChange={setOpen}
 *     onInitiated={() => …}          // called right before Stripe redirects
 *   />
 *
 * Flow:
 *   1. On open, POST /customer/statements/{id}/pay to mint (or reuse)
 *      an on-session PaymentIntent. Response includes client_secret +
 *      publishable_key + reused flag.
 *   2. loadStripe(publishable_key) → Elements → PaymentElement locked
 *      to us_bank_account.
 *   3. "Authorize" → stripe.confirmPayment(); Stripe redirects to
 *      /customer/statements/{id}?pay_return=1 after the FC flow.
 */
import React, { useEffect, useRef, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import {
    Elements,
    PaymentElement,
    useElements,
    useStripe,
} from "@stripe/react-stripe-js";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, AlertTriangle, Building2, Lock, ShieldCheck } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { formatStatementIdentifier } from "@/lib/statementDisplay";

function fmtMoney(cents) {
    if (cents == null) return "—";
    const v = Math.abs(cents) / 100;
    return `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function PayInner({ statement, onInitiated, closeModal }) {
    const stripe = useStripe();
    const elements = useElements();
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [elementReady, setElementReady] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!stripe || !elements || submitting) return;
        setSubmitting(true);
        setError(null);
        try {
            const returnUrl = `${window.location.origin}/customer/statements/${statement.id}?pay_return=1`;
            onInitiated && onInitiated();
            const result = await stripe.confirmPayment({
                elements,
                confirmParams: { return_url: returnUrl },
            });
            if (result?.error) {
                setError(
                    result.error.message ||
                        "We couldn't submit your bank authorization. Please try again.",
                );
            }
        } catch (err) {
            setError(err?.message || "Unexpected error submitting payment.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <form
            onSubmit={handleSubmit}
            className="space-y-4"
            data-testid="statement-pay-form"
        >
            <div className="rounded-lg border border-slate-200 bg-white p-3">
                <PaymentElement
                    options={{
                        layout: "tabs",
                        paymentMethodOrder: ["us_bank_account"],
                        wallets: { applePay: "never", googlePay: "never" },
                        fields: { billingDetails: { address: "never" } },
                    }}
                    onReady={() => setElementReady(true)}
                    onLoadError={(ev) =>
                        setError(
                            ev?.error?.message ||
                                "Stripe Elements failed to load. Refresh and try again.",
                        )
                    }
                />
            </div>

            <div className="rounded-md bg-slate-50 ring-1 ring-slate-200 p-3 text-xs text-slate-600 leading-relaxed">
                <p className="flex items-start gap-2">
                    <ShieldCheck size={14} className="mt-0.5 text-emerald-700 shrink-0" />
                    <span>
                        By authorizing, you allow RentFlow and its payments
                        partner Stripe to debit the bank account selected for{" "}
                        <strong>{fmtMoney(statement.total_cents)}</strong>{" "}
                        against consolidated statement{" "}
                        <strong>{formatStatementIdentifier(statement)}</strong>. ACH
                        debits typically settle in 3–5 business days. You may
                        revoke this authorization by contacting us before the
                        debit posts.
                    </span>
                </p>
            </div>

            {error && (
                <div
                    className="rounded-md bg-red-50 ring-1 ring-red-200 p-3 text-sm text-red-800 flex items-start gap-2"
                    data-testid="statement-pay-error"
                >
                    <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                    <span>{error}</span>
                </div>
            )}

            <DialogFooter className="gap-2 pt-1">
                <Button
                    type="button"
                    variant="ghost"
                    onClick={closeModal}
                    disabled={submitting}
                    data-testid="statement-pay-cancel"
                >
                    Cancel
                </Button>
                <Button
                    type="submit"
                    disabled={!stripe || !elements || submitting || !elementReady}
                    className="bg-blue-900 hover:bg-blue-800 gap-2 min-w-[160px]"
                    data-testid="statement-pay-authorize"
                >
                    {submitting ? (
                        <>
                            <Loader2 size={14} className="animate-spin" /> Authorizing…
                        </>
                    ) : (
                        <>
                            <Lock size={14} /> Authorize {fmtMoney(statement.total_cents)}
                        </>
                    )}
                </Button>
            </DialogFooter>
        </form>
    );
}


export default function StatementPayModal({
    statement,
    open,
    onOpenChange,
    onInitiated,
}) {
    const [boot, setBoot] = useState({
        loading: false, error: null,
        clientSecret: null, stripePromise: null,
    });
    const fetchedFor = useRef(null);

    useEffect(() => {
        if (!open || !statement?.id) return;
        if (fetchedFor.current === statement.id) return;
        fetchedFor.current = statement.id;
        setBoot({
            loading: true, error: null,
            clientSecret: null, stripePromise: null,
        });
        (async () => {
            try {
                const { data } = await api.post(
                    `/customer/statements/${statement.id}/pay`,
                );
                if (!data?.client_secret || !data?.publishable_key) {
                    throw new Error(
                        "Stripe payment session was not configured. Please contact support.",
                    );
                }
                const stripePromise = loadStripe(data.publishable_key);
                setBoot({
                    loading: false, error: null,
                    clientSecret: data.client_secret, stripePromise,
                });
            } catch (err) {
                setBoot({
                    loading: false, error: formatApiError(err),
                    clientSecret: null, stripePromise: null,
                });
            }
        })();
    }, [open, statement?.id]);

    useEffect(() => {
        if (!open) {
            fetchedFor.current = null;
            setBoot({
                loading: false, error: null,
                clientSecret: null, stripePromise: null,
            });
        }
    }, [open]);

    const close = () => onOpenChange && onOpenChange(false);

    if (!statement) return null;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="sm:max-w-[520px]"
                data-testid="statement-pay-modal"
            >
                <DialogHeader>
                    <DialogTitle className="font-display text-xl text-slate-900 flex items-center gap-2">
                        <Building2 size={18} className="text-blue-900" />
                        Pay {fmtMoney(statement.total_cents)} with bank account
                    </DialogTitle>
                    <DialogDescription className="text-slate-500">
                        Statement{" "}
                        <span className="font-mono text-slate-700">
                            {formatStatementIdentifier(statement)}
                        </span>{" "}
                        · ACH bank transfer. Settlement typically takes 3–5 business days.
                    </DialogDescription>
                </DialogHeader>

                {boot.loading && (
                    <div
                        className="flex items-center gap-2 text-sm text-slate-500 py-8 justify-center"
                        data-testid="statement-pay-loading"
                    >
                        <Loader2 size={14} className="animate-spin" />
                        Preparing secure bank-connect flow…
                    </div>
                )}

                {boot.error && (
                    <div
                        className="rounded-md bg-red-50 ring-1 ring-red-200 p-3 text-sm text-red-800 flex items-start gap-2"
                        data-testid="statement-pay-boot-error"
                    >
                        <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                        <span>{boot.error}</span>
                    </div>
                )}

                {boot.clientSecret && boot.stripePromise && (
                    <Elements
                        stripe={boot.stripePromise}
                        options={{
                            clientSecret: boot.clientSecret,
                            appearance: {
                                theme: "stripe",
                                variables: {
                                    colorPrimary: "#1e3a8a",
                                    colorText: "#0f172a",
                                    fontFamily:
                                        'Inter, ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif',
                                    borderRadius: "8px",
                                },
                            },
                        }}
                    >
                        <PayInner
                            statement={statement}
                            onInitiated={onInitiated}
                            closeModal={close}
                        />
                    </Elements>
                )}
            </DialogContent>
        </Dialog>
    );
}
